import { FC } from 'react';
import { Divider, Grid } from '@hopskipdrive/react-components';
import pluralize from 'pluralize';

import { RiderServices, SERVICE_ICON_CODES } from '../../../../../src/riders/features/RiderServices';
import { useTimezone } from '../../../../hooks/useTimezone';
import { formatDate } from '../../../../utils/helpers/formatDate';

import { CancellationReason } from './CancellationReason';
import { PerRiderFareOverview } from './PerRiderFareOverview';
import { PerTripFareOverview } from './PerTripFareOverview';
import { RideOverviewItem } from './RideOverviewItem';

interface RideOverviewInfoProps {
  rideInfo?: {
    distance: number | undefined;
    duration: number | undefined;
    estimatedFare?: string;
    createdAt?: Date;
    updatedAt?: string;
    canceledAt?: string;
    status?: string;
    cancellationReason?: string;
    totalFareWithCredits?: string;
    rideCredits?: {
      amount: number;
      display: string;
      kind: string;
      label: string;
    }[];
    perRiderFareDetails?: PerRiderFareDetails;
    riderServices?: RiderService[];
    services?: {
      name: string;
      code: string;
      enabled?: boolean;
    }[];
  };
  rideDetails?: RideDetails;
  driverInfo?: Driver;
  boosters: number;
  ridersCount: number;
}

export const RideOverviewTab: FC<RideOverviewInfoProps> = ({
  rideInfo,
  boosters,
  ridersCount,
  driverInfo,
  rideDetails,
}) => {
  const isFetching = !!(rideInfo?.createdAt && !rideInfo?.estimatedFare && !rideInfo?.perRiderFareDetails?.fareTotal);
  const { getDateInTimezone } = useTimezone();
  const canceledAt = rideDetails?.canceledAt ? getDateInTimezone(rideDetails?.canceledAt) : '';

  const allRiders = rideDetails?.waypoints?.flatMap((w) => w.riders) || []; // Collect all riders from all waypoints

  const availableServices = rideDetails
    ? allRiders.flatMap((rider) => rider.services ?? [])
    : rideInfo?.riderServices ?? [];
  const services = SERVICE_ICON_CODES.flatMap((code) => {
    const service = availableServices.find((item) => item.code === code);

    return service ? [service] : [];
  });

  const noRiderServicesAvailable = services.length === 0;

  return (
    <>
      <Grid container alignItems="flex-end" className="top-row">
        <Grid item xs={12} lg={3}>
          <RideOverviewItem isFetching={isFetching} value={`${rideInfo?.distance || 0} mi`} label="est. distance" />
        </Grid>
        <Grid item xs={12} lg={3}>
          <RideOverviewItem isFetching={isFetching} value={`${rideInfo?.duration || 0} min`} label="est. duration" />
        </Grid>
        <Grid item xs={12} md={3} data-testid="riderinfo-booster-count">
          <RideOverviewItem
            isFetching={isFetching}
            value={boosters || (ridersCount > 0 ? 'No' : '-')}
            label={`${pluralize('booster', boosters)}`}
          />
        </Grid>
        <Grid item xs={12} lg={3} data-testid="riderinfo-caredriver">
          <RideOverviewItem
            isFetching={isFetching}
            value={driverInfo?.fullName || 'Confirmed'}
            label="CareDriver"
            valueClassName="driver"
          />
        </Grid>
      </Grid>

      <>
        <Divider />
        <Grid container alignItems="flex-end" className="bottom-row">
          <Grid item xs={12} lg={3} data-testid="riderinfo-services">
            <RideOverviewItem
              isFetching={isFetching}
              value={noRiderServicesAvailable ? 'No' : <RiderServices services={services} />}
              label="services"
            />
          </Grid>
          <Grid item xs={12} lg={3} data-testid="riderinfo-riders-count">
            <RideOverviewItem
              isFetching={isFetching}
              value={ridersCount}
              label={ridersCount === 1 ? 'rider' : 'riders'}
            />
          </Grid>
        </Grid>{' '}
      </>

      {rideDetails && rideDetails.cancellationReason ? (
        <CancellationReason
          canceledAt={formatDate(canceledAt, 'EEE, MM/dd/yy, hh:mm aa')}
          reason={rideDetails?.cancellationReason}
        />
      ) : null}
      {rideInfo && rideInfo.perRiderFareDetails && (
        <PerRiderFareOverview
          isFetching={isFetching}
          perRiderFareDetails={rideInfo.perRiderFareDetails}
          rideDetails={rideDetails}
        />
      )}
      {rideInfo && !rideInfo.perRiderFareDetails && <PerTripFareOverview isFetching={isFetching} rideInfo={rideInfo} />}
    </>
  );
};
